import type { AccountInfo } from "@azure/msal-browser";
import { InteractionStatus } from "@azure/msal-browser";
import { useAccount, useMsal } from "@azure/msal-react";
import { useQueryClient } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import { useCallback } from "react";
import { apiScopes } from "~auth/config.ts";
import { useToast } from "~components/shared/useToast.tsx";
import { useTranslation } from "~contexts/I18nContext/I18nContext.tsx";
import { isRedirectNavigationTimeout } from "./isRedirectNavigationTimeout.ts";

export function useAuth() {
  const { instance, accounts, inProgress } = useMsal();
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  const { showErrorToast } = useToast();
  const t = useTranslation();

  const startRedirect = useCallback(
    async (navigate: () => Promise<void>, failureMessage: string) => {
      try {
        await navigate();
      } catch (error) {
        if (!isRedirectNavigationTimeout(error)) {
          throw error;
        }
        showErrorToast(failureMessage);
      }
    },
    [showErrorToast],
  );

  const switchUser = useCallback(
    async (account: AccountInfo) => {
      instance.setActiveAccount(account);
      await queryClient.resetQueries({ queryKey: [] });
      posthog.identify(account.username, { email: account.username });
    },
    [instance, queryClient, posthog],
  );

  const loginAdditionalUser = useCallback(async () => {
    await queryClient.resetQueries({ queryKey: [] });
    await startRedirect(
      () =>
        instance.loginRedirect({
          scopes: apiScopes(),
          prompt: "select_account",
        }),
      t.loginFailed,
    );
  }, [instance, queryClient, startRedirect, t]);

  const login = useCallback(async () => {
    if (inProgress === InteractionStatus.None) {
      await startRedirect(
        () => instance.loginRedirect({ scopes: apiScopes() }),
        t.loginFailed,
      );
    }
  }, [inProgress, instance, startRedirect, t]);

  const logout = useCallback(async () => {
    queryClient.getQueryCache().clear();
    const account = instance.getActiveAccount();
    await startRedirect(
      () => instance.logoutRedirect({ account }),
      t.logoutFailed,
    );
  }, [instance, queryClient, startRedirect, t]);

  const activeAccount = useAccount();
  const inactiveAccounts = accounts.filter(
    (a) => a.username !== activeAccount?.username,
  );

  return {
    switchUser,
    activeAccount,
    inactiveAccounts,
    login,
    loginAdditionalUser,
    logout,
    inProgress,
  };
}
